import React from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    VStack,
    HStack,
    Flex,
    Box,
    Text,
    Badge,
    Icon,
    IconButton,
    Avatar,
    Divider,
    Button,
    Image,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from "@chakra-ui/react";
import {
    MdMoreVert,
    MdCalendarToday,
    MdPhone,
    MdEmail,
    MdVisibility,
} from "react-icons/md";
import LoadingSpinner from "components/LoadingSpinner";
import starIcon from "assets/img/icons/Avis/star.png";
import starEmptyIcon from "assets/img/icons/Avis/star-empty.png";

import { useNavigate } from "react-router-dom";

const AvisDetailModal = ({
    isOpen,
    onClose,
    viewData,
    loadingDetails,
    patientData,
    missionData,
}) => {
    const navigate = useNavigate();
    const handleVoirMission = () => {
        console.log('MISSION_ID:', missionData?.id);
        if (missionData?.id) {
            onClose && onClose();
            setTimeout(() => {
                navigate(`/admin/missions/${String(missionData.id)}`);
            }, 0);
        }
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent borderRadius="12px" maxW="700px">
                <ModalHeader borderBottom="1px solid" borderColor="gray.100" py={4}>
                    <Text fontSize="md" fontWeight="bold">Détails de l'avis</Text>
                </ModalHeader>
                <ModalCloseButton mt={1} />
                <ModalBody p={6}>
                    {loadingDetails ? (
                        <LoadingSpinner message="Chargement des détails..." size="md" />
                    ) : (
                        viewData &&
                        (() => {
                            const status = viewData.statut || "visible";
                            const clientName = patientData
                                ? `${patientData.nom || ""} ${patientData.prenom || ""}`.trim()
                                : "Client inconnu";

                            return (
                                <VStack align="stretch" spacing={6}>
                                    {/* Profile Section */}
                                    <Box position="relative" textAlign="center" pt={4}>
                                        {/* Status Badge Top Right */}
                                        <HStack position="absolute" top={0} right={0} spacing={2}>
                                            <Badge
                                                bg="#EBFBEE"
                                                color="#2F9461"
                                                px={3}
                                                py={1}
                                                borderRadius="full"
                                                display="flex"
                                                alignItems="center"
                                                textTransform="none"
                                                fontSize="xs"
                                                fontWeight="medium"
                                                border="1px solid"
                                                borderColor="#D1FADF"
                                            >
                                                <Box w="6px" h="6px" borderRadius="full" bg="#2F9461" mr={2} />
                                                Visible
                                            </Badge>
                                            <Menu>
                                                <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" aria-label="Options" />
                                                <MenuList>
                                                    <MenuItem icon={<MdVisibility />} onClick={handleVoirMission}>
                                                        Voir mission
                                                    </MenuItem>
                                                </MenuList>
                                            </Menu>
                                        </HStack>

                                        <VStack spacing={3}>
                                            {patientData?.logo ? (
                                                <Image
                                                    src={patientData.logo}
                                                    alt={clientName}
                                                    w="80px"
                                                    h="80px"
                                                    borderRadius="8px"
                                                    objectFit="cover"
                                                />
                                            ) : (
                                                <Avatar size="xl" name={clientName} borderRadius="8px" />
                                            )}
                                            <Text fontSize="lg" fontWeight="bold" color="gray.900">
                                                {clientName}
                                            </Text>

                                            {/* Rating Box */}
                                            <HStack
                                                border="1px dashed"
                                                borderColor="gray.300"
                                                borderRadius="12px"
                                                px={4}
                                                py={2}
                                                spacing={2}
                                            >
                                                <Text fontSize="sm" color="gray.600">Avis</Text>
                                                <Text fontSize="sm" fontWeight="bold">{parseFloat(viewData.étoile).toFixed(1)}</Text>
                                                <HStack spacing={1}>
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <Image
                                                            key={star}
                                                            src={parseFloat(viewData.étoile) >= star ? starIcon : starEmptyIcon}
                                                            w="14px"
                                                            h="14px"
                                                        />
                                                    ))}
                                                </HStack>
                                            </HStack>
                                        </VStack>
                                    </Box>

                                    {/* Badges Info Row */}
                                    <Flex wrap="wrap" justify="center" gap={3}>
                                        <HStack bg="gray.50" px={3} py={1.5} borderRadius="6px" color="gray.600" fontSize="xs">
                                            <Text color="gray.400">#</Text>
                                            <Text fontWeight="medium">AV{String(viewData.id).padStart(2, "0")}</Text>
                                        </HStack>
                                        <HStack bg="gray.50" px={3} py={1.5} borderRadius="6px" color="gray.600" fontSize="xs">
                                            <Icon as={MdCalendarToday} color="gray.400" />
                                            <Text fontWeight="medium">
                                                {new Date(viewData.créé_le).toLocaleDateString("fr-FR")} {new Date(viewData.créé_le).toLocaleTimeString("fr-FR", { hour: '2-digit', minute: '2-digit' })}
                                            </Text>
                                        </HStack>
                                        <HStack bg="gray.50" px={3} py={1.5} borderRadius="6px" color="gray.600" fontSize="xs">
                                            <Icon as={MdPhone} color="gray.400" />
                                            <Text fontWeight="medium">{patientData?.téléphone || "N/A"}</Text>
                                        </HStack>
                                        <HStack bg="gray.50" px={3} py={1.5} borderRadius="6px" color="gray.600" fontSize="xs">
                                            <Icon as={MdEmail} color="gray.400" />
                                            <Text fontWeight="medium">{patientData?.email || "N/A"}</Text>
                                        </HStack>
                                    </Flex>

                                    <Divider />

                                    {/* Comment Section - ONLY THE TEXT */}
                                    <VStack align="stretch" spacing={4}>
                                        <Text fontSize="sm" color="gray.800" fontWeight="medium">
                                            {viewData.avi || "Aucun commentaire"}
                                        </Text>
                                    </VStack>
                                </VStack>
                            );
                        })()
                    )}
                </ModalBody>
                <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        size="md"
                        fontSize="sm"
                        px={8}
                    >
                        Fermer
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AvisDetailModal;
